import React, { useEffect, useState, useRef } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  mergeClasses,
  Subtitle2,
  Caption1,
  Body1,
} from "@fluentui/react-components";
import {
  DeleteRegular,
  ErrorCircleRegular,
  CheckmarkCircleFilled,
} from "@fluentui/react-icons";
import { useDispatch } from "react-redux";
import {
  scanFile,
  uploadFile,
  removeFile as removeFileRedux,
  clearLoading,
} from "../../redux/S3FileUpload/fileUploadSlice";
import { AppDispatch } from "../../redux/store";

const useStyles = makeStyles({
  container: { display: "flex", flexDirection: "row", flexWrap: "wrap", ...shorthands.gap("12px"), maxWidth: "100%" },
  fileCard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.padding("12px"),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    transition: "all 0.2s ease",
    minWidth: "220px",
    boxSizing: "border-box",
  },
  leftSection: { display: "flex", flexDirection: "row", alignItems: "center", ...shorthands.gap("12px"), flexGrow: 1 },
  infoSection: { display: "flex", flexDirection: "column" },
  iconContainer: { display: "flex", alignItems: "center", justifyContent: "center", minWidth: "28px" },

  // Status Styles
  default: { ...shorthands.border("1px", "solid", tokens.colorNeutralStroke1), backgroundColor: tokens.colorNeutralBackground1 },
  error: { backgroundColor: "#fff8f8", ...shorthands.border("1px", "solid", "#ef9a9a"), color: "#d32f2f" },
  success: { backgroundColor: "#f8fff8", ...shorthands.border("1px", "solid", "#a5d6a7"), color: "#2e7d32" },
  scanning: { backgroundColor: "#f8fff8", ...shorthands.border("1px", "solid", "#ffcc80"), color: "#ed6c02" },

  strikeThrough: { textDecorationLine: "line-through" },

  // Custom Spinner for precise color control
  customSpinner: {
    width: "20px",
    height: "20px",
    border: "2px solid #eee",
    borderRadius: "50%",
    animationName: {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
    animationDuration: "0.8s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
  }
});

type FileStatus = "uploading" | "scanning" | "success" | "error";
interface FileWithStatus { id: string; objectId?: string; file: File; status: FileStatus; }

const DynamicDropzone = ({ multiple = false, maxFiles = 5, onFilesChange, externalFiles = [], userId, style }: any) => {
  const styles = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const [files, setFiles] = useState<FileWithStatus[]>([]);

  const processedFilesRef = useRef<Set<File>>(new Set());
  const abortControllers = useRef<{ [key: string]: AbortController }>({});
  const pollingTimeouts = useRef<{ [key: string]: any }>({});

  const cleanUpResources = (id: string) => {
    if (abortControllers.current[id]) abortControllers.current[id].abort();
    if (pollingTimeouts.current[id]) clearTimeout(pollingTimeouts.current[id]);
    delete abortControllers.current[id];
    delete pollingTimeouts.current[id];
  };

  const updateStatus = (id: string, status: FileStatus, objectId?: string) => {
    setFiles((prev) => {
      const updated = prev.map((f) => (f.id === id ? { ...f, status, ...(objectId && { objectId }) } : f));
      if (onFilesChange) onFilesChange(updated);
      return updated;
    });
  };

  const pollScanStatus = async (objectId: string, tempId: string) => {
    let attempts = 0;
    const MAX_ATTEMPTS = 30;

    const checkStatus = async () => {
      if (!abortControllers.current[tempId]) return;
      attempts++;

      if (attempts > MAX_ATTEMPTS) {
        updateStatus(tempId, "error");
        return;
      }

      const resultAction = await dispatch(
        scanFile({ objectId, tempId, signal: abortControllers.current[tempId].signal })
      );

      // CASE A: Fulfilled (Check API payload)
      if (scanFile.fulfilled.match(resultAction)) {
        // Handle both single object or array response
        const data = Array.isArray(resultAction.payload) ? resultAction.payload[0] : resultAction.payload;
        const status = data?.scanStatus;
        const isError = data?.isError;

        if (status === "SUCCESS") {
          updateStatus(tempId, "success");
        } else if (status === "FAILED" || status === "ERROR" || isError) {
          updateStatus(tempId, "error");
          cleanUpResources(tempId);
        } else {
          // It's PENDING - call again in 10s
          pollingTimeouts.current[tempId] = setTimeout(checkStatus, 10000);
        }
      }
      // CASE B: Rejected (Network/Server Error)
      else if (scanFile.rejected.match(resultAction)) {
        const errorPayload: any = resultAction.payload;
        if (errorPayload?.statusCode === 500 || errorPayload?.code === 1001) {
          updateStatus(tempId, "error");
        } else {
          // Retry on minor glitches
          pollingTimeouts.current[tempId] = setTimeout(checkStatus, 10000);
        }
      }
    };

    checkStatus();
  };

  useEffect(() => {
    // Reconcile: abort and remove any internal file entries no longer in externalFiles
    const externalSet = new Set(externalFiles);
    setFiles(prev => {
      const stale = prev.filter(f => !externalSet.has(f.file));
      if (stale.length === 0) return prev;
      stale.forEach(f => {
        cleanUpResources(f.id);
        processedFilesRef.current.delete(f.file);
      });
      return prev.filter(f => externalSet.has(f.file));
    });

    const handleProcess = async () => {
      const newFiles = externalFiles.filter((f: File) => !processedFilesRef.current.has(f));

      for (const file of newFiles) {
        processedFilesRef.current.add(file);

        const tempId = `${file.name}-${Date.now()}`;
        abortControllers.current[tempId] = new AbortController();

        const fileObj: FileWithStatus = { id: tempId, file, status: "uploading" };
        setFiles(prev => multiple ? [...prev, fileObj] : [fileObj]);

        const uploadAction = await dispatch(
          uploadFile({ file, tempId, userId, signal: abortControllers.current[tempId].signal })
        );

        if (uploadFile.fulfilled.match(uploadAction)) {
          const objectId = uploadAction.payload?.data?.[0]?.objectId
          if (objectId) {
            updateStatus(tempId, "scanning", objectId);
            pollScanStatus(objectId, tempId);
          } else {
            updateStatus(tempId, "error");
          }
        } else {
          updateStatus(tempId, "error");
        }
      }
    };

    if (externalFiles.length > 0) handleProcess();
    return () => Object.keys(abortControllers.current).forEach(cleanUpResources);
  }, [externalFiles]);

  const removeFile = (id: string) => {
    cleanUpResources(id);
    dispatch(removeFileRedux(id));
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file) processedFilesRef.current.delete(file.file);
      const updated = prev.filter(f => f.id !== id);
      if (onFilesChange) onFilesChange(updated);
      if (updated.length === 0) dispatch(clearLoading());
      return updated;
    });
  };

  return (
    <div className={styles.container} style={style}>
      {files.map((item) => (
        <div key={item.id} className={mergeClasses(styles.fileCard, styles[item.status as keyof typeof styles] || styles.default)} style={!multiple ? { width: "100%", boxSizing: "border-box" } : {}}>
          <div className={styles.leftSection}>
            <div className={styles.iconContainer}>
              {item.status === "error" && <ErrorCircleRegular style={{ fontSize: "24px" }} />}
              {item.status === "success" && <CheckmarkCircleFilled style={{ fontSize: "22px" }} />}
              {(item.status === "uploading" || item.status === "scanning") && (
                <div
                  className={styles.customSpinner}
                  style={{ borderTopColor: item.status === "scanning" ? "#ed6c02" : tokens.colorBrandStroke1 }}
                />
              )}
            </div>
            <div className={styles.infoSection}>
              <Body1 className={item.status === "error" ? styles.strikeThrough : ""}>{item.file.name}</Body1>
              <Caption1>
                {(item.file.size / 1024 / 1024).toFixed(3)} MB •
                {item.status === "uploading" && " Uploading..."}
                {item.status === "scanning" && " Scanning for threats..."}
                {item.status === "success" && " Clean"}
                {item.status === "error" && " Failed security validation"}
              </Caption1>
            </div>
          </div>
          <Button appearance="subtle" icon={<DeleteRegular />} onClick={() => removeFile(item.id)} />
        </div>
      ))}
    </div>
  );
};

export default DynamicDropzone;